/**
 * Stockout Alert Detail Dialog
 * =============================
 * Shows comprehensive details about why a SKU was flagged for stockout risk
 */

import React from 'react';
import { X, AlertTriangle, TrendingDown, Package, Warehouse, Activity, DollarSign, Info } from 'lucide-react';
import { StockoutPrediction } from '../hooks/useStockoutPredictions';

interface StockoutAlertDetailDialogProps {
  prediction: StockoutPrediction;
  onClose: () => void;
  currentFiscalWeek?: number;
}

export default function StockoutAlertDetailDialog({ prediction, onClose, currentFiscalWeek }: StockoutAlertDetailDialogProps) {
  // Check if prediction data is stale
  const predictionWeek = parseInt(prediction.fiscal_week?.toString() || '0');
  const selectedWeek = currentFiscalWeek || predictionWeek;
  const isStale = predictionWeek > 0 && selectedWeek > 0 && Math.abs(predictionWeek - selectedWeek) > 1;
  const weeksDifference = Math.abs(selectedWeek - predictionWeek);
  
  // Generate detailed explanation
  const getDetailedExplanation = () => {
    const reasons = [];
    const currentOOS = prediction.current_state.oos_pct || 0;
    const currentWOS = prediction.current_state.store_wos || 0;
    const dcWOS = prediction.current_state.dc_wos || 0;
    const velocity = prediction.current_state.velocity || 0;
    
    // Reason 1: Predicted OOS%
    if (prediction.predicted_oos_pct >= 50) {
      reasons.push({
        severity: 'critical',
        title: 'Very High Stockout Probability',
        description: `Model predicts ${prediction.predicted_oos_pct.toFixed(1)}% CHANCE of stockout in 2 weeks. This indicates severe inventory shortage is imminent.`,
        impact: 'Extremely likely to lose sales opportunities'
      });
    } else if (prediction.predicted_oos_pct >= 30) {
      reasons.push({
        severity: 'high',
        title: 'High Stockout Probability',
        description: `Model predicts ${prediction.predicted_oos_pct.toFixed(1)}% CHANCE of stockout in 2 weeks based on inventory trends.`,
        impact: 'Significant risk of revenue loss'
      });
    } else if (prediction.predicted_oos_pct >= 10) {
      reasons.push({
        severity: 'medium',
        title: 'Moderate Stockout Probability',
        description: `Model predicts ${prediction.predicted_oos_pct.toFixed(1)}% CHANCE of stockout in 2 weeks.`,
        impact: 'Measurable revenue at risk'
      });
    }
    
    // Reason 2: Current OOS% Status
    if (currentOOS >= 10) {
      reasons.push({
        severity: 'critical',
        title: 'Already Experiencing Stockouts',
        description: `Current OOS% is ${currentOOS.toFixed(1)}%, indicating stores are already out of stock. Trend shows this will worsen.`,
        impact: 'Immediate revenue loss occurring now'
      });
    } else if (currentOOS >= 5) {
      reasons.push({
        severity: 'medium',
        title: 'Elevated Current OOS%',
        description: `Current OOS% is ${currentOOS.toFixed(1)}%, above healthy threshold of 5%.`,
        impact: 'Early warning signal - trending worse'
      });
    }
    
    // Reason 3: Low WOS
    if (currentWOS < 2) {
      reasons.push({
        severity: 'critical',
        title: 'Critically Low Store Inventory',
        description: `Only ${currentWOS.toFixed(1)} weeks of supply left in stores. This will run out before next shipment arrives.`,
        impact: 'Imminent stockout - reorder urgently'
      });
    } else if (currentWOS < 4) {
      reasons.push({
        severity: 'medium',
        title: 'Low Store Inventory',
        description: `${currentWOS.toFixed(1)} weeks of supply in stores is below recommended 4-week buffer.`,
        impact: 'Risk of stockout if demand spikes or shipment delays'
      });
    }
    
    // Reason 4: DC/Supply Chain Issues
    if (dcWOS < 2 && currentWOS < 4) {
      reasons.push({
        severity: 'critical',
        title: 'Supply Chain Bottleneck',
        description: `DC has only ${dcWOS.toFixed(1)} weeks of supply. Cannot replenish stores quickly enough.`,
        impact: 'Stores will run out before DC can restock them'
      });
    }
    
    // Reason 5: High Velocity + Low Inventory
    if (velocity > 100 && currentWOS < 3) {
      reasons.push({
        severity: 'high',
        title: 'High Demand vs Low Inventory',
        description: `Moving ${velocity.toFixed(0)} units/week but only ${currentWOS.toFixed(1)} weeks of supply. High velocity is depleting inventory faster than expected.`,
        impact: 'Fast-moving product will run out quickly'
      });
    }
    
    if (reasons.length === 0) {
      reasons.push({
        severity: 'info',
        title: 'Statistical Pattern Detection',
        description: 'Model identified historical patterns indicating increased stockout risk based on similar past scenarios.',
        impact: 'Preventive alert based on trend analysis'
      });
    }
    
    return reasons;
  };

  const reasons = getDetailedExplanation();

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-50 border-red-200';
      case 'high': return 'bg-orange-50 border-orange-200';
      case 'medium': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'high': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'medium': return <Info className="w-5 h-5 text-yellow-600" />;
      default: return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {prediction.product_name || prediction.upc}
            </h2>
            <p className="text-sm text-gray-600">SKU #{prediction.upc}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Staleness Warning */}
        {isStale && (
          <div className="mx-6 mt-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Prediction Data is {weeksDifference} Week(s) Old</div>
                <div className="text-sm text-amber-800 mt-1">
                  This prediction is based on week {predictionWeek} data. Current week is {selectedWeek}. 
                  The "Current Inventory Status" shown below reflects week {predictionWeek}, not real-time data.
                  Actual current state may differ significantly.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Risk Summary */}
          <div className={`rounded-lg p-4 border-2 ${
            prediction.risk_level === 'critical' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className={`w-6 h-6 ${
                prediction.risk_level === 'critical' ? 'text-red-600' : 'text-yellow-600'
              }`} />
              <div>
                <div className="font-bold text-lg text-gray-900">
                  {prediction.predicted_oos_pct.toFixed(1)}% Predicted Out-of-Stock
                </div>
                <div className="text-sm text-gray-700">In 2 weeks (targeting future week)</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Revenue at Risk</div>
                <div className="text-2xl font-bold text-gray-900">
                  ${prediction.revenue_at_risk.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Risk Level</div>
                <div className="text-2xl font-bold text-gray-900 uppercase">{prediction.risk_level}</div>
              </div>
            </div>
          </div>

          {/* Why This Alert Was Triggered */}
          <div>
            <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-gray-900" />
              Why This Alert Was Triggered
            </h3>
            <div className="space-y-3">
              {reasons.map((reason, idx) => (
                <div key={idx} className={`rounded-lg p-4 border ${getSeverityColor(reason.severity)}`}>
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(reason.severity)}
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 mb-1">{reason.title}</div>
                      <div className="text-sm text-gray-700 mb-2">{reason.description}</div>
                      <div className="text-xs text-gray-600 italic">💡 Impact: {reason.impact}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Inventory Status */}
          <div>
            <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-gray-900" />
              Current Inventory Status
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-600 uppercase mb-1">
                  Store WOS {isStale && `(Week ${predictionWeek})`}
                </div>
                <div className={`text-xl font-bold ${
                  (prediction.current_state.store_wos || 0) < 2 ? 'text-red-600' : 
                  (prediction.current_state.store_wos || 0) < 4 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {prediction.current_state.store_wos ? `${prediction.current_state.store_wos.toFixed(1)}w` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Target: 4w minimum
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-600 uppercase mb-1">DC WOS</div>
                <div className={`text-xl font-bold ${
                  (prediction.current_state.dc_wos || 0) < 2 ? 'text-red-600' : 
                  (prediction.current_state.dc_wos || 0) < 4 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {prediction.current_state.dc_wos ? `${prediction.current_state.dc_wos.toFixed(1)}w` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Supply chain buffer
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-600 uppercase mb-1">
                  {isStale ? `OOS% Week ${predictionWeek}` : 'Current OOS%'}
                </div>
                <div className={`text-xl font-bold ${
                  (prediction.current_state.oos_pct || 0) >= 10 ? 'text-red-600' : 
                  (prediction.current_state.oos_pct || 0) >= 5 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {prediction.current_state.oos_pct ? `${prediction.current_state.oos_pct.toFixed(1)}%` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {isStale ? 'Historical data' : 'Current state'}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-600 uppercase mb-1">Velocity</div>
                <div className="text-xl font-bold text-gray-900">
                  {prediction.current_state.velocity ? `${prediction.current_state.velocity.toFixed(0)} u/w` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Units per week
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-600 uppercase mb-1">Store EOH</div>
                <div className="text-xl font-bold text-gray-900">
                  {prediction.current_state.store_eoh ? prediction.current_state.store_eoh.toLocaleString() : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Units on hand
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-600 uppercase mb-1">DC EOH</div>
                <div className="text-xl font-bold text-gray-900">
                  {prediction.current_state.dc_eoh ? prediction.current_state.dc_eoh.toLocaleString() : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Distribution center
                </div>
              </div>
            </div>
          </div>

          {/* Recommended Actions */}
          <div>
            <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-gray-900" />
              Recommended Actions
            </h3>
            <div className="space-y-3">
              {prediction.predicted_oos_pct >= 50 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="font-bold text-red-900 mb-2">🚨 URGENT: Expedite Reorder</div>
                  <div className="text-sm text-red-800">
                    Contact supplier immediately for expedited shipping. Current inventory will not last 2 weeks.
                    Consider reallocating stock from other locations if available.
                  </div>
                </div>
              )}
              
              {(prediction.current_state.store_wos || 0) < 2 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="font-bold text-orange-900 mb-2">⚡ Priority Reorder</div>
                  <div className="text-sm text-orange-800">
                    Immediate reorder needed. Calculate: Velocity × (4 weeks - {(prediction.current_state.store_wos || 0).toFixed(1)} weeks) = 
                    {prediction.current_state.velocity ? 
                      ` ~${Math.ceil(prediction.current_state.velocity * (4 - (prediction.current_state.store_wos || 0)))} units` 
                      : ' order quantity'}
                  </div>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="font-bold text-blue-900 mb-2">📊 Monitor Closely</div>
                <div className="text-sm text-blue-800">
                  Track this SKU daily. Check store-by-store inventory to identify specific locations at risk.
                  Set up automatic alerts if OOS% exceeds {((prediction.current_state.oos_pct || 0) + 5).toFixed(0)}%.
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="font-bold text-green-900 mb-2">✅ Verify with Supply Chain</div>
                <div className="text-sm text-green-800">
                  Confirm on-order quantities and expected delivery dates. 
                  Ensure shipments are scheduled to arrive before stockout occurs.
                </div>
              </div>
            </div>
          </div>

          {/* Model Confidence */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-bold text-purple-900 mb-3">🤖 Model Confidence</h3>
            <div className="text-sm text-purple-800 space-y-2">
              <div>
                <strong>Accuracy:</strong> This model has 83.33% accuracy validated on 49 weeks of historical data.
              </div>
              <div>
                <strong>Precision:</strong> When it predicts a stockout, it's correct 84.4% of the time (low false alarm rate).
              </div>
              <div>
                <strong>Features Used:</strong> Prediction based on 15 key features including current inventory, historical trends, velocity, and rolling averages.
              </div>
              <div className="pt-2 border-t border-purple-200">
                <strong>What This Means:</strong> There's a strong probability this SKU will experience stockout issues. 
                The model has been validated to catch 82% of actual stockouts 2 weeks in advance.
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Close
          </button>
          <button
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            onClick={() => {
              // Future: Navigate to product detail or create order
              alert('Reorder functionality coming soon');
            }}
          >
            Create Reorder
          </button>
        </div>
      </div>
    </div>
  );
}
